import { useRef, useState } from 'react';
import { DropTargetMonitor, useDrag, useDrop } from 'react-dnd';
import { 
  Box, 
  IconButton, 
  Typography, 
  Chip, 
  TextField, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button 
} from "@mui/material";
import { Delete, DragIndicator, Edit } from "@mui/icons-material";

// Define a constant for the draggable item type
const ItemTypes = {
  IMAGE: 'image'
};

// Define the props for the component
export interface DraggableImageItemProps {
  image: any;
  index: number;
  moveImage: (dragIndex: number, hoverIndex: number) => void;
  onTagChange: (imageIdx: number, newTag: string) => void;
  onDelete: (imageIdx: number) => void;
}

const DraggableImageItem = ({ 
  image, 
  index, 
  moveImage, 
  onTagChange, 
  onDelete,
}: DraggableImageItemProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isTagDialogOpen, setTagDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [tagValue, setTagValue] = useState(image.imageTag || '');

  const [{ isDragging }, drag] = useDrag<{ index: number }, void, { isDragging: boolean }>({
    type: ItemTypes.IMAGE,
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: ItemTypes.IMAGE,
    hover: (item: { index: number }, monitor: DropTargetMonitor) => {
      if (!ref.current) {
        return;
      }
      
      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) {
        return;
      }

      const hoverBoundingRect = ref.current.getBoundingClientRect();
      const dragThreshold = 0.25;
      const hoverTopY = hoverBoundingRect.height * dragThreshold;
      const hoverBottomY = hoverBoundingRect.height * (1 - dragThreshold);
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset!.y - hoverBoundingRect.top;

      if (dragIndex < hoverIndex && hoverClientY < hoverTopY) {
        return;
      }
      
      if (dragIndex > hoverIndex && hoverClientY > hoverBottomY) {
        return;
      }

      moveImage(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  // Attach both drag and drop refs to the same element
  drag(drop(ref));

  // --- Event Handlers ---
  const handleTagEdit = () => {
    setTagValue(image.imageTag || '');
    setTagDialogOpen(true);
  };
  
  const handleTagSave = () => {
    onTagChange(index, tagValue);
    setTagDialogOpen(false);
  };
  
  const handleTagCancel = () => setTagDialogOpen(false);
  
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleTagSave();
    else if (e.key === 'Escape') handleTagCancel();
  };

  const handleDeleteClick = () => setDeleteDialogOpen(true);
  const handleDeleteConfirm = () => {
    onDelete(index);
    setDeleteDialogOpen(false);
  };
  const handleDeleteCancel = () => setDeleteDialogOpen(false);

  return (
    <Box 
      ref={ref} 
      sx={{ 
        display: "flex", 
        alignItems: "center", 
        p: 1, 
        borderRadius: 1, 
        bgcolor: image.isNew ? "rgba(46, 125, 50, 0.08)" : "background.paper", 
        boxShadow: 1, 
        opacity: isDragging ? 0.5 : 1, 
        cursor: 'move', 
        mb: 2,
        border: "2px solid red"
      }}
    >
      <DragIndicator color="action" sx={{ mr: 1 }} />
      <img 
        className="image-order-list-img" 
        src={image.imageUrlCf} 
        alt={image.imageTag || `Image ${index + 1}`} 
        style={{ maxHeight: 100, objectFit: "contain" }} 
      />
      <Box sx={{ flexGrow: 1, ml: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1, minHeight: '20px' }}>
            {image.imageTag || 'No tag'}
          </Typography>
          <IconButton size="small" onClick={handleTagEdit}><Edit fontSize="small" /></IconButton>
          <IconButton size="small" onClick={handleDeleteClick} color="error"><Delete fontSize="small" /></IconButton>
        </Box>
        {image.isNew && <Chip label="New" color="success" size="small" />}
      </Box>

      {/* --- Dialogs for Editing and Deleting --- */}
      <Dialog open={isTagDialogOpen} onClose={handleTagCancel} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Image Tag</DialogTitle>
        <DialogContent>
          <TextField value={tagValue} onChange={(e) => setTagValue(e.target.value)} onKeyDown={handleKeyPress} fullWidth autoFocus margin="normal" />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleTagCancel}>Cancel</Button>
          <Button onClick={handleTagSave} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
      
      <Dialog open={isDeleteDialogOpen} onClose={handleDeleteCancel} maxWidth="sm">
        <DialogTitle>Delete Image</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this image? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} variant="contained" color="error">Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DraggableImageItem;